"use client";

import { User } from "@/types/user";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useDeleteUser } from "@/hooks/use-users";
import { toast } from "sonner";

interface DeleteUserDialogProps {
  user: User | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function DeleteUserDialog({
  user,
  open,
  onOpenChange,
  onSuccess,
}: DeleteUserDialogProps) {
  // The API accepts users with no school, so schoolId is only used to scope
  // cache invalidation for the school roster the user may have appeared in.
  const deleteUserMutation = useDeleteUser(user?.schoolId ?? undefined);

  const handleDelete = async () => {
    if (!user) return;

    try {
      await deleteUserMutation.mutateAsync(user.id);

      toast.success(
        `${user.firstName} ${user.lastName} has been deleted.`,
      );

      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Failed to delete user. Please try again.",
      );
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete User?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                This will delete{" "}
                <span className="font-medium text-foreground">
                  {user?.firstName} {user?.lastName}
                </span>{" "}
                (ID {user?.id})
                {user?.schoolId ? (
                  <>
                    {" "}
                    from{" "}
                    <span className="font-medium text-foreground">
                      {user.schoolId}
                    </span>
                  </>
                ) : null}
                .
              </p>
              <p>
                Their email and phone number are cleared immediately, which frees
                those up for a new account and cannot be undone. The account is
                then fully removed by a background job.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleteUserMutation.isPending}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              // Keep the dialog mounted while the request is in flight so the
              // pending state is visible; we close it ourselves on success.
              e.preventDefault();
              handleDelete();
            }}
            disabled={deleteUserMutation.isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {deleteUserMutation.isPending ? "Deleting..." : "Delete User"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
